import React, { useMemo, useRef } from 'react';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import {
  RichTextEditorComponent as RTE,
  Toolbar as RTEToolbar,
  Link as RteLink,
  Image as RteImage,
  HtmlEditor,
  QuickToolbar,
  Table,
  EmojiPicker,
  PasteCleanup,
  Count,
  Inject as RTEInject,
} from '@syncfusion/ej2-react-richtexteditor';
import { RTE_QUICK_TOOLBAR_SETTINGS, getRteToolbarSettingsSimple } from '../../utils/rteConfig';
import './EmployerLayoutRenderer.css';

/* ================================================================
   EmployerLayoutRenderer
   Props:
     layoutId   : 'layout-a' | 'layout-b' | 'layout-c' | 'layout-d'
     sections   : Section[]  (key, contentData, title, …)
     isEditMode : boolean
     onUpdateField(sectionKey, fieldName, value)  — called on blur
     onUploadImage(sectionKey, fieldName, file)    — called when user picks a file
     uploadingFields : Set of 'sectionKey.fieldName' strings currently uploading
   ================================================================ */
export default function EmployerLayoutRenderer({
  layoutId,
  sections = [],
  isEditMode = false,
  onUpdateField,
  onUploadImage,
  uploadingFields = new Set(),
}) {
  const normalizeSectionTitle = (title = '') =>
    String(title).replace(/\s+section\s*$/i, '').trim();

  const getSec = (key) => sections.find((s) => s.key === key) || { key, contentData: null };
  const cd = (key) => {
    const section = getSec(key);
    return {
      ...(section.contentData || {}),
      isActive: section.isActive !== false,
    };
  };

  const getNavItems = () =>
    NAV_SECTIONS
      .map((item, index) => {
        const section = getSec(item.key);
        const headingFromContent = section?.contentData?.sectionHeading;
        // Prefer saved order; fall back to position in the sections payload
        // (public API may omit order on older responses), then NAV_SECTIONS index.
        const sectionIndex = sections.findIndex((s) => s.key === item.key);
        const order = typeof section?.order === 'number'
          ? section.order
          : (sectionIndex >= 0 ? sectionIndex : index);
        return {
          ...item,
          title: headingFromContent
            || normalizeSectionTitle(section?.title || item.label || '')
            || item.label,
          order,
          isActive: section?.isActive !== false,
        };
      })
      .filter((item) => {
        if (!item.isActive) return false;
        if (isEditMode) return true;
        return hasRenderableSectionContent(item.key, getSec(item.key));
      })
      .sort((a, b) => a.order - b.order);

  const commonProps = { isEditMode, onUpdateField, onUploadImage, uploadingFields, getNavItems };

  const layoutMap = {
    'layout-a': (
      <LayoutA cd={cd} getSec={getSec} {...commonProps} />
    ),
    'layout-b': (
      <LayoutB cd={cd} getSec={getSec} {...commonProps} />
    ),
    'layout-c': (
      <LayoutC cd={cd} getSec={getSec} {...commonProps} />
    ),
    'layout-d': (
      <LayoutD cd={cd} getSec={getSec} {...commonProps} />
    ),
  };

  return (
    <div className={`elr-wrap${isEditMode ? ' elr-edit-mode' : ''}`}>
      {layoutMap[layoutId] || layoutMap['layout-a']}
    </div>
  );
}

/* ================================================================
   Shared sub-components
   ================================================================ */
const NAV_SECTIONS = [
  { key: 'about', label: 'About', anchor: 'about' },
  { key: 'program', label: 'Programs', anchor: 'programs' },
  { key: 'video', label: 'Video', anchor: 'video' },
  { key: 'gallery', label: 'Gallery', anchor: 'gallery' },
  { key: 'jobs', label: 'Careers', anchor: 'careers' },
  { key: 'benefits', label: 'Benefits', anchor: 'benefits' },
  { key: 'contact', label: 'Contact', anchor: 'contact' },
];

function renderNavLinks(prefix, navItems, linkClassName = '') {
  return navItems.map((item) => (
    <a key={item.key} href={`#${prefix}-${item.anchor}`} className={linkClassName}>
      {item.title}
    </a>
  ));
}

const hasText = (value) => typeof value === 'string' && value.trim().length > 0;
const stripHtml = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').trim();
const hasHtmlText = (value) => stripHtml(value).length > 0;
const hasAnyImage = (images = []) => Array.isArray(images) && images.some((img) => hasText(img?.url));
const hasActionLink = (text, url) => hasText(text) && hasText(url) && url.trim() !== '#';
const IMAGE_FIT_PRESETS = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
];

function hasRenderableSectionContent(sectionKey, section) {
  const data = section?.contentData || {};
  switch (sectionKey) {
    case 'about':
      return (
        hasText(data.companyName)
        || hasText(data.tagline)
        || hasHtmlText(data.aboutText)
        || hasText(data.heroImageUrl)
        || hasText(data.brandImage1Url)
        || hasText(data.brandImage2Url)
      );
    case 'program':
      return hasText(data.programTitle) || hasHtmlText(data.programText) || hasText(data.programImageUrl);
    case 'video':
      return hasText(data.videoTitle) || hasText(data.videoUrl);
    case 'gallery':
      return hasText(data.galleryTitle) || hasAnyImage(data.images);
    case 'jobs':
      return (Array.isArray(data.jobsList) && data.jobsList.length > 0) || hasText(data.locationsText);
    case 'benefits':
      return hasHtmlText(getBenefitsHtml(data));
    case 'contact':
      return (
        hasText(data.sectionHeading)
        || hasText(data.ctaHeadline)
        || hasActionLink(data.primaryBtnText, data.primaryBtnUrl)
        || hasActionLink(data.secondaryBtnText, data.secondaryBtnUrl)
      );
    default:
      return false;
  }
}

/** Clickable image zone: shows image or placeholder; in edit mode lets user upload */
function ImgZone({
  sectionKey,
  fieldName,
  url,
  alt,
  altValue,
  label,
  className,
  style,
  isEditMode,
  onUploadImage,
  onUpdateField,
  uploadingFields,
  resolveFitMode,
  fitFallback = 'cover',
}) {
  const inputRef = useRef(null);
  const uploadKey = `${sectionKey}.${fieldName}`;
  const isUploading = uploadingFields.has(uploadKey);
  const fitFieldName = `${fieldName}__fit`;
  const displayAlt = (typeof altValue === 'string' && altValue.trim()) ? altValue.trim() : (alt || label);
  const altPlaceholder = `Alt text — describe ${String(label).split('·')[0].trim() || 'this image'}`;
  const resolvedFitMode = resolveFitMode
    ? resolveFitMode(sectionKey, fieldName, fitFallback)
    : fitFallback;
  const activeFitMode = IMAGE_FIT_PRESETS.some((preset) => preset.value === resolvedFitMode) ? resolvedFitMode : 'cover';

  // In live view, do not render empty image placeholders.
  if (!isEditMode && !url) {
    return null;
  }

  const handleClick = () => {
    if (isEditMode) inputRef.current?.click();
  };
  const handleKeyDown = (e) => {
    if (isEditMode && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      inputRef.current?.click();
    }
  };
  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUploadImage) onUploadImage(sectionKey, fieldName, file);
    e.target.value = '';
  };
  const removeImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUpdateField && onUpdateField(sectionKey, fieldName, '');
  };
  const updateFitMode = (e, nextFitMode) => {
    e.preventDefault();
    e.stopPropagation();
    onUpdateField && onUpdateField(sectionKey, fitFieldName, nextFitMode);
  };
  const handleAltChange = (nextAlt) => {
    if (!onUpdateField) return;
    // Gallery images store url + alt together on the same images.N object.
    if (fieldName.startsWith('images.')) {
      onUpdateField(sectionKey, fieldName, { url, alt: nextAlt });
      return;
    }
    // Other images keep alt in a sibling field: heroImageUrl -> heroImageAlt, etc.
    onUpdateField(sectionKey, fieldName.replace(/Url$/, 'Alt'), nextAlt);
  };

  if (url) {
    return (
      <div
        className={`elr-img-zone${className ? ` ${className}` : ''}`}
        style={style}
        onClick={isEditMode ? handleClick : undefined}
        onKeyDown={isEditMode ? handleKeyDown : undefined}
        role={isEditMode ? 'button' : undefined}
        tabIndex={isEditMode ? 0 : undefined}
        aria-label={isEditMode ? `Change ${label} image` : displayAlt}
      >
        <img src={url} alt={displayAlt} style={{ objectFit: activeFitMode }} />
        {isEditMode && (
          <div className="elr-img-overlay" aria-hidden="true">
            <div className="elr-img-overlay-icon">✎</div>
            <span className="elr-img-overlay-text">Change image</span>
          </div>
        )}
        {isEditMode && (
          <div
            className="elr-img-alt-bar"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <span className="elr-img-alt-tag" aria-hidden="true">ALT</span>
            <input
              type="text"
              className="elr-img-alt-input"
              defaultValue={typeof altValue === 'string' ? altValue : ''}
              placeholder={altPlaceholder}
              onBlur={(e) => handleAltChange(e.target.value)}
              aria-label={`Alt text for ${label}`}
            />
          </div>
        )}
        {isEditMode && (
          <div className="elr-img-bottom-actions">
            <button
              type="button"
              className="elr-img-remove-btn"
              onClick={removeImage}
              aria-label={`Remove ${label} image`}
            >
              Remove
            </button>
            <div className="elr-img-fit-switch" role="toolbar" aria-label={`${label} fit mode`}>
              {IMAGE_FIT_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`elr-img-fit-btn${activeFitMode === preset.value ? ' active' : ''}`}
                  onClick={(e) => updateFitMode(e, preset.value)}
                  aria-pressed={activeFitMode === preset.value}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {isUploading && (
          <div className="elr-img-uploading">
            <div className="elr-spinner" />
          </div>
        )}
        {isEditMode && (
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleChange}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`elr-ph${className ? ` ${className}` : ''}`}
      style={style}
      onClick={isEditMode ? handleClick : undefined}
      onKeyDown={isEditMode ? handleKeyDown : undefined}
      role={isEditMode ? 'button' : undefined}
      tabIndex={isEditMode ? 0 : undefined}
      aria-label={isEditMode ? `Upload ${label} image` : label}
    >
      <span className="elr-ph-label">{label}</span>
      {isUploading && (
        <div className="elr-img-uploading">
          <div className="elr-spinner" />
        </div>
      )}
      {isEditMode && (
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleChange}
        />
      )}
    </div>
  );
}

/** Inline editable text span. */
function TXT({ sectionKey, fieldName, value, placeholder, className, style, tagName, isEditMode, onUpdateField, colorValue }) {
  const Tag = tagName || 'span';
  const hasValue = hasText(value);
  const appliedColor = colorValue || undefined;

  const syncEditableEmptyState = (el) => {
    const isEmpty = !(el.textContent || '').trim();
    el.setAttribute('data-empty', isEmpty ? 'true' : 'false');
  };

  const colorStyle = appliedColor ? { ...style, color: appliedColor } : style;

  if (isEditMode) {
    const editableEl = (
      <Tag
        className={`elr-text-editable${!hasValue ? ' elr-text-empty' : ''}${className ? ` ${className}` : ''}`}
        style={colorStyle}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => syncEditableEmptyState(e.currentTarget)}
        onFocus={(e) => syncEditableEmptyState(e.currentTarget)}
        onBlur={(e) => {
          syncEditableEmptyState(e.currentTarget);
          onUpdateField && onUpdateField(sectionKey, fieldName, e.currentTarget.textContent || '');
        }}
        data-placeholder={placeholder}
        data-empty={hasValue ? 'false' : 'true'}
        aria-label={placeholder}
      >
        {value || ''}
      </Tag>
    );

    return editableEl;
  }

  if (!hasValue) return null;

  return (
    <Tag className={className} style={colorStyle}>
      {value}
    </Tag>
  );
}

/**
 * Font sizes for template body text. Syncfusion's default list is in points
 * (8–36 pt) and has no 16px, which is the page's paragraph size.
 */
const TEMPLATE_RTE_FONT_SIZE = {
  items: [
    { text: 'Default', value: '' },
    { text: '12 px', value: '12px' },
    { text: '14 px', value: '14px' },
    { text: '16 px', value: '16px' },
    { text: '18 px', value: '18px' },
    { text: '20 px', value: '20px' },
    { text: '24 px', value: '24px' },
    { text: '28 px', value: '28px' },
    { text: '32 px', value: '32px' },
    { text: '36 px', value: '36px' },
  ],
};

/** Syncfusion rich-text body field for longer template copy. */
function RichBody({
  sectionKey,
  fieldName,
  value,
  placeholder,
  className,
  style,
  isEditMode,
  onUpdateField,
}) {
  const toolbarSettings = useMemo(() => getRteToolbarSettingsSimple(), []);
  const hasValue = hasHtmlText(value);

  if (isEditMode) {
    return (
      <div
        className={`elr-rte-field${className ? ` ${className}` : ''}`}
        style={style}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <RTE
          value={value || ''}
          change={(e) => onUpdateField && onUpdateField(sectionKey, fieldName, e?.value || '')}
          toolbarSettings={toolbarSettings}
          quickToolbarSettings={RTE_QUICK_TOOLBAR_SETTINGS}
          fontSize={TEMPLATE_RTE_FONT_SIZE}
          height={220}
          placeholder={placeholder}
          enableXhtml
          aria-label={placeholder}
        >
          <RTEInject services={[HtmlEditor, RTEToolbar, QuickToolbar, RteLink, RteImage, Table, EmojiPicker, PasteCleanup, Count]} />
        </RTE>
      </div>
    );
  }

  if (!hasValue) return null;

  return (
    <div
      className={`elr-body elr-rte-html${className ? ` ${className}` : ''}`}
      style={style}
      dangerouslySetInnerHTML={{ __html: value }}
    />
  );
}

/** Video embed or placeholder */
function VideoZone({ sectionKey, url, isEditMode, onUpdateField, className }) {
  if (url) {
    const embedUrl = getYouTubeEmbedUrl(url) || url;
    return (
      <div className={`elr-a-video-zone${className ? ` ${className}` : ''}`} style={{ position: 'relative' }}>
        <iframe
          src={embedUrl}
          title="Employer video"
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0, border: 'none' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
        {isEditMode && (
          <div style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.5)', borderRadius: '0 0 0 6px', padding: '4px 8px', zIndex: 10 }}>
            <span style={{ color: '#fff', fontSize: 11, fontFamily: 'monospace' }}>Click URL below to edit</span>
          </div>
        )}
      </div>
    );
  }
  if (!isEditMode) {
    return null;
  }
  return (
    <div className={`elr-ph${className ? ` ${className}` : ''}`} style={{ width: '100%', aspectRatio: '16/9', maxWidth: 800, margin: '0 auto', position: 'relative' }}>
      <span className="elr-ph-label">
        {isEditMode
          ? 'Paste a YouTube URL in the Video URL field below'
          : 'YouTube Video Embed · 16:9'}
      </span>
      <div className="elr-play-btn" aria-hidden="true">
        <div className="elr-play-arrow" />
      </div>
    </div>
  );
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  // Build a clean embed URL to avoid carrying problematic query params.
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

function extractYouTubeVideoId(rawUrl) {
  const fallbackMatch = String(rawUrl).match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([a-zA-Z0-9_-]{11})/
  );

  try {
    const parsed = new URL(String(rawUrl).trim());
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname || '';

    if (host === 'youtu.be') {
      const id = path.replace('/', '').split('/')[0];
      if (/^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
    }

    const isYouTubeHost = host.includes('youtube.com') || host.includes('youtube-nocookie.com');
    if (!isYouTubeHost) return fallbackMatch?.[1] || null;

    if (path === '/watch') {
      const id = parsed.searchParams.get('v');
      if (/^[a-zA-Z0-9_-]{11}$/.test(id || '')) return id;
    }

    const pathMatch = path.match(/\/(?:embed|shorts|live)\/([a-zA-Z0-9_-]{11})/);
    if (pathMatch) return pathMatch[1];

    return fallbackMatch?.[1] || null;
  } catch {
    return fallbackMatch?.[1] || null;
  }
}

function getDefaultCopyrightText() {
  return `© ${new Date().getFullYear()} Employer. All Rights Reserved.`;
}

function getSocialPlaceholder(platform) {
  const placeholders = {
    FB: 'https://facebook.com/company/...',
    IN: 'https://linkedin.com/company/...',
    IG: 'https://instagram.com/company/...',
    TH: 'https://threads.net/@company',
    YT: 'https://youtube.com/@company',
    TT: 'https://tiktok.com/@company',
    BS: 'https://bsky.app/profile/company.bsky.social',
    X: 'https://x.com/company',
  };
  return placeholders[platform] || 'https://example.com/company';
}

/* ================================================================
   Section color controls
   ================================================================ */
const BG_PRESETS = [
  { label: 'White', value: '#ffffff' },
  { label: 'Off-white', value: '#f4f2ee' },
  { label: 'Light blue', value: '#eef2f9' },
  { label: 'Light gray', value: '#f3f4f6' },
  { label: 'Warm gray', value: '#e8e6e1' },
  { label: 'Dark navy', value: '#1c2533' },
  { label: 'Black', value: '#111827' },
];

const TEXT_PRESETS = [
  { label: 'Dark', value: '#1a1a1a' },
  { label: 'Gray', value: '#555555' },
  { label: 'White', value: '#ffffff' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Muted', value: '#6b7280' },
];

function getSectionStyle(data) {
  const s = {};
  if (data?.bgColor) s.background = data.bgColor;
  if (data?.textColor) s.color = data.textColor;
  return s;
}

/**
 * Style for the menu/header (logo + nav). Kept independent of the hero/about
 * section colors so the navigation bar can be themed separately. When no
 * menu-specific colors are set the header keeps its default CSS theme.
 */
function getMenuStyle(data) {
  const s = {};
  if (data?.menuBgColor) s.background = data.menuBgColor;
  if (data?.menuTextColor) s.color = data.menuTextColor;
  return s;
}

/* Heading style options. Keys are stored in contentData and mapped to CSS here,
   so unknown or stale values fall back to the layout's defaults. */
const HEADING_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const HEADING_FONT_OPTIONS = [
  { label: 'Default (Georgia)', value: '', css: '' },
  { label: 'Arial', value: 'arial', css: 'Arial, Helvetica, sans-serif' },
  { label: 'Verdana', value: 'verdana', css: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'tahoma', css: 'Tahoma, Geneva, sans-serif' },
  { label: 'Trebuchet MS', value: 'trebuchet', css: '"Trebuchet MS", Helvetica, sans-serif' },
  { label: 'Times New Roman', value: 'times', css: '"Times New Roman", Times, serif' },
  { label: 'Courier New', value: 'courier', css: '"Courier New", Courier, monospace' },
];

const HEADING_SIZE_OPTIONS = [
  { label: 'Default', value: '', scale: 1 },
  { label: 'Small', value: 'sm', scale: 0.85 },
  { label: 'Large', value: 'lg', scale: 1.2 },
  { label: 'Extra large', value: 'xl', scale: 1.4 },
];

const HEADING_WEIGHT_OPTIONS = [
  { label: 'Default', value: '', weight: '', style: '' },
  { label: 'Regular', value: 'regular', weight: '400', style: 'normal' },
  { label: 'Bold', value: 'bold', weight: '700', style: 'normal' },
  { label: 'Italic', value: 'italic', weight: '400', style: 'italic' },
  { label: 'Bold italic', value: 'bold-italic', weight: '700', style: 'italic' },
];

const HEADING_STYLE_FIELDS = ['headingAlign', 'headingFont', 'headingSize', 'headingWeight'];

const SECTION_LABELS = {
  about: 'About',
  program: 'Programs',
  video: 'Video',
  gallery: 'Gallery',
  jobs: 'Careers',
  benefits: 'Benefits',
  contact: 'Contact',
};

/** Class names and CSS variables that apply a section's heading style to its headings. */
function getHeadingStyleProps(data) {
  const className = [];
  const style = {};
  if (HEADING_ALIGN_OPTIONS.some((o) => o.value === data?.headingAlign)) {
    className.push(`elr-hs-align-${data.headingAlign}`);
  }
  const font = HEADING_FONT_OPTIONS.find((o) => o.value && o.value === data?.headingFont);
  if (font) style['--elr-heading-font'] = font.css;
  const size = HEADING_SIZE_OPTIONS.find((o) => o.value && o.value === data?.headingSize);
  if (size) style['--elr-heading-scale'] = size.scale;
  const weight = HEADING_WEIGHT_OPTIONS.find((o) => o.value && o.value === data?.headingWeight);
  if (weight) {
    style['--elr-heading-weight'] = weight.weight;
    style['--elr-heading-style'] = weight.style;
  }
  return { className: className.join(' '), style };
}

function getImageFitMode(sectionData, fieldName, fallback = 'cover') {
  const raw = sectionData?.[`${fieldName}__fit`];
  if (IMAGE_FIT_PRESETS.some((preset) => preset.value === raw)) {
    return raw;
  }
  return fallback;
}

/** Thin color control bar shown at the top of each section in edit mode */
function ColorBar({ sectionKey, data, onUpdateField }) {
  const bgColor = data?.bgColor || '';
  const headingColor = data?.headingColor || '';
  const textColor = data?.textColor || '';
  const hasCustom = bgColor || headingColor || textColor;
  return (
    <div className="elr-color-bar" role="toolbar" aria-label={`Color settings for ${sectionKey} section`}>
      <span className="elr-color-bar-label">Background</span>
      {BG_PRESETS.map((p) => (
        <button
          key={p.value}
          type="button"
          className={`elr-color-swatch${bgColor === p.value ? ' elr-swatch-active' : ''}`}
          style={{ background: p.value }}
          title={p.label}
          onClick={() => onUpdateField(sectionKey, 'bgColor', bgColor === p.value ? '' : p.value)}
          aria-label={`Background: ${p.label}`}
          aria-pressed={bgColor === p.value}
        />
      ))}
      <label className="elr-color-custom-wrap" title="Custom background color">
        <span
          className="elr-color-swatch elr-color-custom-swatch"
          style={{ background: bgColor && !BG_PRESETS.find((p) => p.value === bgColor) ? bgColor : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
          aria-hidden="true"
        />
        <input
          type="color"
          value={bgColor || '#ffffff'}
          onChange={(e) => onUpdateField(sectionKey, 'bgColor', e.target.value)}
          aria-label="Custom background color"
        />
      </label>

      <span className="elr-color-bar-sep" aria-hidden="true" />

      <span className="elr-color-bar-label">Heading</span>
      {TEXT_PRESETS.map((p) => (
        <button
          key={`heading-${p.value}`}
          type="button"
          className={`elr-color-swatch${headingColor === p.value ? ' elr-swatch-active' : ''}`}
          style={{ background: p.value }}
          title={p.label}
          onClick={() => onUpdateField(sectionKey, 'headingColor', headingColor === p.value ? '' : p.value)}
          aria-label={`Heading: ${p.label}`}
          aria-pressed={headingColor === p.value}
        />
      ))}
      <label className="elr-color-custom-wrap" title="Custom heading color">
        <span
          className="elr-color-swatch elr-color-custom-swatch"
          style={{ background: headingColor && !TEXT_PRESETS.find((p) => p.value === headingColor) ? headingColor : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
          aria-hidden="true"
        />
        <input
          type="color"
          value={headingColor || '#1a1a1a'}
          onChange={(e) => onUpdateField(sectionKey, 'headingColor', e.target.value)}
          aria-label="Custom heading color"
        />
      </label>

      <span className="elr-color-bar-sep" aria-hidden="true" />

      <span className="elr-color-bar-label">Text</span>
      {TEXT_PRESETS.map((p) => (
        <button
          key={p.value}
          type="button"
          className={`elr-color-swatch${textColor === p.value ? ' elr-swatch-active' : ''}`}
          style={{ background: p.value }}
          title={p.label}
          onClick={() => onUpdateField(sectionKey, 'textColor', textColor === p.value ? '' : p.value)}
          aria-label={`Text: ${p.label}`}
          aria-pressed={textColor === p.value}
        />
      ))}
      <label className="elr-color-custom-wrap" title="Custom text color">
        <span
          className="elr-color-swatch elr-color-custom-swatch"
          style={{ background: textColor && !TEXT_PRESETS.find((p) => p.value === textColor) ? textColor : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
          aria-hidden="true"
        />
        <input
          type="color"
          value={textColor || '#1a1a1a'}
          onChange={(e) => onUpdateField(sectionKey, 'textColor', e.target.value)}
          aria-label="Custom text color"
        />
      </label>

      {hasCustom && (
        <button
          type="button"
          className="elr-color-reset"
          onClick={() => {
            onUpdateField(sectionKey, 'bgColor', '');
            onUpdateField(sectionKey, 'headingColor', '');
            onUpdateField(sectionKey, 'textColor', '');
          }}
          title="Reset to default colors"
        >
          ↺
        </button>
      )}
    </div>
  );
}

/** Heading alignment, font, size and weight for one section, shown in edit mode */
function HeadingStyleBar({ sectionKey, data, onUpdateField }) {
  const sectionLabel = SECTION_LABELS[sectionKey] || sectionKey;
  const align = data?.headingAlign || '';
  const hasCustom = HEADING_STYLE_FIELDS.some((field) => data?.[field]);
  const selectValue = (options, value) => (options.some((o) => o.value === value) ? value : '');

  return (
    <div className="elr-color-bar elr-heading-bar" role="group" aria-label={`Heading style for ${sectionLabel} section`}>
      <span className="elr-color-bar-label" aria-hidden="true">Heading</span>
      <div className="elr-heading-align" role="group" aria-label="Heading alignment">
        {HEADING_ALIGN_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`elr-heading-btn${align === o.value ? ' active' : ''}`}
            onClick={() => onUpdateField(sectionKey, 'headingAlign', align === o.value ? '' : o.value)}
            aria-pressed={align === o.value}
            aria-label={`Align headings ${o.label.toLowerCase()}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <span className="elr-color-bar-sep" aria-hidden="true" />

      <label className="elr-heading-field">
        <span>Font</span>
        <select
          value={selectValue(HEADING_FONT_OPTIONS, data?.headingFont)}
          onChange={(e) => onUpdateField(sectionKey, 'headingFont', e.target.value)}
        >
          {HEADING_FONT_OPTIONS.map((o) => (
            <option key={o.value || 'default'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="elr-heading-field">
        <span>Size</span>
        <select
          value={selectValue(HEADING_SIZE_OPTIONS, data?.headingSize)}
          onChange={(e) => onUpdateField(sectionKey, 'headingSize', e.target.value)}
        >
          {HEADING_SIZE_OPTIONS.map((o) => (
            <option key={o.value || 'default'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      <label className="elr-heading-field">
        <span>Style</span>
        <select
          value={selectValue(HEADING_WEIGHT_OPTIONS, data?.headingWeight)}
          onChange={(e) => onUpdateField(sectionKey, 'headingWeight', e.target.value)}
        >
          {HEADING_WEIGHT_OPTIONS.map((o) => (
            <option key={o.value || 'default'} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      {hasCustom && (
        <button
          type="button"
          className="elr-color-reset"
          onClick={() => HEADING_STYLE_FIELDS.forEach((field) => onUpdateField(sectionKey, field, ''))}
        >
          Reset heading style
        </button>
      )}
    </div>
  );
}

/** Color control bar for the menu/header bar (independent of the hero colors) */
function MenuColorBar({ data, onUpdateField }) {
  const menuBgColor = data?.menuBgColor || '';
  const menuTextColor = data?.menuTextColor || '';
  const hasCustom = menuBgColor || menuTextColor;
  return (
    <div className="elr-color-bar" role="toolbar" aria-label="Color settings for menu bar">
      <span className="elr-color-bar-label">Menu Background</span>
      {BG_PRESETS.map((p) => (
        <button
          key={`menu-bg-${p.value}`}
          type="button"
          className={`elr-color-swatch${menuBgColor === p.value ? ' elr-swatch-active' : ''}`}
          style={{ background: p.value }}
          title={p.label}
          onClick={() => onUpdateField('about', 'menuBgColor', menuBgColor === p.value ? '' : p.value)}
          aria-label={`Menu background: ${p.label}`}
          aria-pressed={menuBgColor === p.value}
        />
      ))}
      <label className="elr-color-custom-wrap" title="Custom menu background color">
        <span
          className="elr-color-swatch elr-color-custom-swatch"
          style={{ background: menuBgColor && !BG_PRESETS.find((p) => p.value === menuBgColor) ? menuBgColor : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
          aria-hidden="true"
        />
        <input
          type="color"
          value={menuBgColor || '#ffffff'}
          onChange={(e) => onUpdateField('about', 'menuBgColor', e.target.value)}
          aria-label="Custom menu background color"
        />
      </label>

      <span className="elr-color-bar-sep" aria-hidden="true" />

      <span className="elr-color-bar-label">Menu Text</span>
      {TEXT_PRESETS.map((p) => (
        <button
          key={`menu-text-${p.value}`}
          type="button"
          className={`elr-color-swatch${menuTextColor === p.value ? ' elr-swatch-active' : ''}`}
          style={{ background: p.value }}
          title={p.label}
          onClick={() => onUpdateField('about', 'menuTextColor', menuTextColor === p.value ? '' : p.value)}
          aria-label={`Menu text: ${p.label}`}
          aria-pressed={menuTextColor === p.value}
        />
      ))}
      <label className="elr-color-custom-wrap" title="Custom menu text color">
        <span
          className="elr-color-swatch elr-color-custom-swatch"
          style={{ background: menuTextColor && !TEXT_PRESETS.find((p) => p.value === menuTextColor) ? menuTextColor : 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }}
          aria-hidden="true"
        />
        <input
          type="color"
          value={menuTextColor || '#1a1a1a'}
          onChange={(e) => onUpdateField('about', 'menuTextColor', e.target.value)}
          aria-label="Custom menu text color"
        />
      </label>

      {hasCustom && (
        <button
          type="button"
          className="elr-color-reset"
          onClick={() => {
            onUpdateField('about', 'menuBgColor', '');
            onUpdateField('about', 'menuTextColor', '');
          }}
          title="Reset menu colors"
        >
          ↺
        </button>
      )}
    </div>
  );
}

/**
 * Wrapper that applies per-section bg/text color overrides and renders the
 * ColorBar in edit mode. Use `as` prop to control the HTML element (default: div).
 */
function SecWrap({ sectionKey, cd, isEditMode, onUpdateField, id, className, style, children, as: Tag = 'section', ...rest }) {
  const data = cd(sectionKey);
  if (data?.isActive === false) return null;
  const colorStyle = getSectionStyle(data);
  const headingStyle = data?.headingColor ? { '--elr-heading-color': data.headingColor } : {};
  // The footer (social) section has no headings, so it gets no heading controls.
  const hasHeadings = sectionKey !== 'social';
  const headingProps = hasHeadings ? getHeadingStyleProps(data) : { className: '', style: {} };
  return (
    <Tag
      id={id}
      className={[className, headingProps.className].filter(Boolean).join(' ') || undefined}
      style={{ ...headingStyle, ...headingProps.style, ...colorStyle, ...(style || {}) }}
      {...rest}
    >
      {isEditMode && (
        <div className="elr-section-controls">
          <ColorBar sectionKey={sectionKey} data={data} onUpdateField={onUpdateField} />
          {hasHeadings && (
            <HeadingStyleBar sectionKey={sectionKey} data={data} onUpdateField={onUpdateField} />
          )}
        </div>
      )}
      {children}
    </Tag>
  );
}

/** Jobs list — editable as a textarea in edit mode */
function JobsList({ sectionKey, jobsList, isEditMode, onUpdateField }) {
  const items = Array.isArray(jobsList) ? jobsList : [];
  const half = Math.ceil(items.length / 2);
  const col1 = items.slice(0, half);
  const col2 = items.slice(half);

  if (isEditMode) {
    return (
      <div>
        <p style={{ fontFamily: 'Arial,sans-serif', fontSize: 13, color: '#888', marginBottom: 6 }}>
          Edit jobs (one per line):
        </p>
        <textarea
          defaultValue={items.join('\n')}
          rows={8}
          style={{
            width: '100%',
            fontFamily: 'Arial,sans-serif',
            fontSize: 14,
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '8px 10px',
            resize: 'vertical',
            lineHeight: 1.6,
          }}
          onBlur={(e) => {
            const lines = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean);
            onUpdateField && onUpdateField(sectionKey, 'jobsList', lines);
          }}
          aria-label="Job listings (one per line)"
        />
      </div>
    );
  }

  if (items.length === 0) {
    if (!isEditMode) return null;
    return <p className="elr-body" style={{ color: '#aaa', fontStyle: 'italic' }}>No positions listed yet.</p>;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 3rem' }}>
      <ul className="elr-jobs-list" aria-label="Open positions column 1">
        {col1.map((j, i) => <li key={i}>{j}</li>)}
      </ul>
      <ul className="elr-jobs-list" aria-label="Open positions column 2">
        {col2.map((j, i) => <li key={i}>{j}</li>)}
      </ul>
    </div>
  );
}

/** Convert legacy one-per-line benefitsList into HTML for the rich-text field. */
function benefitsListToHtml(list) {
  if (!Array.isArray(list) || list.length === 0) return '';
  const escape = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return `<ul>${list.map((item) => `<li>${escape(item)}</li>`).join('')}</ul>`;
}

function getBenefitsHtml({ benefitsText, benefitsList } = {}) {
  // Once benefitsText exists (even empty), prefer it so clearing the editor sticks.
  if (typeof benefitsText === 'string') return benefitsText;
  return benefitsListToHtml(benefitsList);
}

/** Benefits — rich-text body (migrates legacy benefitsList arrays). */
function BenefitsList({ sectionKey, benefitsText, benefitsList, isEditMode, onUpdateField }) {
  return (
    <RichBody
      sectionKey={sectionKey}
      fieldName="benefitsText"
      value={getBenefitsHtml({ benefitsText, benefitsList })}
      placeholder="Describe benefits and perks..."
      className="elr-body"
      isEditMode={isEditMode}
      onUpdateField={onUpdateField}
    />
  );
}

/** Social footer links */
function SocialFooter({ sectionKey, links, bgColor, textColor, iconColor, iconBgColor, isEditMode, onUpdateField }) {
  const PLATFORMS = ['FB', 'IN', 'IG', 'TH', 'YT', 'TT', 'BS', 'X'];
  const platformLabels = {
    FB: 'Facebook',
    IN: 'LinkedIn',
    IG: 'Instagram',
    TH: 'Threads',
    YT: 'YouTube',
    TT: 'TikTok',
    BS: 'Bluesky',
    X: 'X (Twitter)',
  };
  const linksByPlatform = new Map((Array.isArray(links) ? links : []).map((item) => [item.platform, item]));
  const safeLinks = PLATFORMS.map((platform) => ({
    platform,
    url: linksByPlatform.get(platform)?.url || '',
  }));
  const platformImageUrls = {
    TH: 'https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/threads.svg',
    TT: 'https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/tiktok.svg',
    BS: 'https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/bluesky.svg',
  };
  const platformIcons = {
    FB: FaFacebookF,
    IN: FaLinkedinIn,
    IG: FaInstagram,
    YT: FaYoutube,
    X: FaXTwitter,
  };
  const resolvedIconColor = iconColor || '#111827';
  const resolvedIconBgColor = iconBgColor || textColor || '#e8e6e1';

  const renderPlatformIcon = (platform) => {
    // Image-based icons are rendered via CSS mask so the icon color applies.
    if (platformImageUrls[platform]) {
      return (
        <span
          className="elr-social-icon-mask"
          style={{
            backgroundColor: resolvedIconColor,
            WebkitMaskImage: `url(${platformImageUrls[platform]})`,
            maskImage: `url(${platformImageUrls[platform]})`,
          }}
          aria-hidden="true"
        />
      );
    }
    const Icon = platformIcons[platform];
    if (!Icon) return <span className="elr-social-icon-label">{platform}</span>;
    return <Icon className="elr-social-icon-svg" aria-hidden="true" focusable="false" />;
  };
  const socialIconStyle = {
    background: resolvedIconBgColor,
    color: resolvedIconColor,
    borderColor: iconBgColor || textColor || '#ccc',
  };

  if (isEditMode) {
    return (
      <div>
        <div className="elr-social-icons" style={{ marginBottom: 12 }}>
          {safeLinks.map((l) => (
            <a key={l.platform} href={l.url || '#'} className="elr-social-icon" style={socialIconStyle} aria-label={platformLabels[l.platform] || l.platform} target="_blank" rel="noopener noreferrer">
              {renderPlatformIcon(l.platform)}
            </a>
          ))}
        </div>
        <div className="elr-social-icon-color" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, justifyContent: 'center' }}>
          <span style={{ fontSize: 12, color: '#555' }}>Icon color</span>
          <label className="elr-color-custom-wrap" title="Custom icon color" style={{ display: 'inline-flex' }}>
            <span
              className="elr-color-swatch elr-color-custom-swatch"
              style={{ background: resolvedIconColor }}
              aria-hidden="true"
            />
            <input
              type="color"
              value={resolvedIconColor}
              onChange={(e) => onUpdateField && onUpdateField(sectionKey, 'iconColor', e.target.value)}
              aria-label="Custom icon color"
            />
          </label>
          {iconColor && (
            <button
              type="button"
              className="elr-color-reset"
              onClick={() => onUpdateField && onUpdateField(sectionKey, 'iconColor', '')}
              title="Reset icon color"
            >
              ↺
            </button>
          )}
          <span className="elr-color-bar-sep" aria-hidden="true" />
          <span style={{ fontSize: 12, color: '#555' }}>Button color</span>
          <label className="elr-color-custom-wrap" title="Custom button background color" style={{ display: 'inline-flex' }}>
            <span
              className="elr-color-swatch elr-color-custom-swatch"
              style={{ background: resolvedIconBgColor }}
              aria-hidden="true"
            />
            <input
              type="color"
              value={resolvedIconBgColor}
              onChange={(e) => onUpdateField && onUpdateField(sectionKey, 'iconBgColor', e.target.value)}
              aria-label="Custom button background color"
            />
          </label>
          {iconBgColor && (
            <button
              type="button"
              className="elr-color-reset"
              onClick={() => onUpdateField && onUpdateField(sectionKey, 'iconBgColor', '')}
              title="Reset button color"
            >
              ↺
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {safeLinks.map((l, idx) => (
            <div key={l.platform} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, width: 28, color: '#888' }}>{l.platform}</span>
              <input
                type="url"
                defaultValue={l.url || ''}
                placeholder={getSocialPlaceholder(l.platform)}
                style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}
                onBlur={(e) => {
                  const updated = [...safeLinks];
                  updated[idx] = { ...updated[idx], url: e.target.value };
                  onUpdateField && onUpdateField(sectionKey, 'links', updated);
                }}
                aria-label={`${platformLabels[l.platform] || l.platform} URL`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeLinks = safeLinks.filter((l) => l.url);
  if (activeLinks.length === 0) return null;
  return (
    <nav className="elr-social-icons" aria-label="Social media links">
      {activeLinks.map((l) => (
        <a key={l.platform} href={l.url} className="elr-social-icon" style={socialIconStyle} aria-label={platformLabels[l.platform] || l.platform} target="_blank" rel="noopener noreferrer">
          {renderPlatformIcon(l.platform)}
        </a>
      ))}
    </nav>
  );
}

/** CTA buttons section (contact) */
function CtaButtons({ sectionKey, contentData, isEditMode, onUpdateField, className }) {
  const {
    primaryBtnText = '',
    primaryBtnUrl = '#',
    secondaryBtnText = '',
    secondaryBtnUrl = '#',
  } = contentData;
  const resolvedPrimaryBtnText = hasText(primaryBtnText) ? primaryBtnText.trim() : 'Join Our Talent Community';
  const resolvedSecondaryBtnText = hasText(secondaryBtnText) ? secondaryBtnText.trim() : 'View Our Open Positions';
  // Only override the button colors when a custom text/accent color is set.
  // Falling back to `currentColor` made the primary button resolve to its own
  // white text color (invisible on light backgrounds), so default to the CSS.
  const accentColor = contentData?.textColor || '';
  const primaryBtnStyle = accentColor
    ? { background: accentColor, borderColor: accentColor }
    : undefined;
  const outlineBtnStyle = accentColor
    ? { color: accentColor, borderColor: accentColor }
    : undefined;

  if (isEditMode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <input
            type="text"
            defaultValue={primaryBtnText || ''}
            placeholder="Primary button text"
            style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}
            onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'primaryBtnText', e.target.value)}
            aria-label="Primary button text"
          />
          <input
            type="url"
            defaultValue={primaryBtnUrl !== '#' ? primaryBtnUrl : ''}
            placeholder="Primary button URL"
            style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}
            onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'primaryBtnUrl', e.target.value || '#')}
            aria-label="Primary button URL"
          />
          <input
            type="text"
            defaultValue={secondaryBtnText || ''}
            placeholder="Secondary button text"
            style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}
            onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'secondaryBtnText', e.target.value)}
            aria-label="Secondary button text"
          />
          <input
            type="url"
            defaultValue={secondaryBtnUrl !== '#' ? secondaryBtnUrl : ''}
            placeholder="Secondary button URL"
            style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}
            onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'secondaryBtnUrl', e.target.value || '#')}
            aria-label="Secondary button URL"
          />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
          <span className="elr-btn-primary" style={primaryBtnStyle}>{resolvedPrimaryBtnText}</span>
          <span className="elr-btn-outline" style={outlineBtnStyle}>{resolvedSecondaryBtnText}</span>
        </div>
      </div>
    );
  }

  const liveButtons = [
    hasActionLink(primaryBtnText, primaryBtnUrl)
      ? { key: 'primary', href: primaryBtnUrl.trim(), text: primaryBtnText.trim(), className: 'elr-btn-primary', style: primaryBtnStyle }
      : null,
    hasActionLink(secondaryBtnText, secondaryBtnUrl)
      ? { key: 'secondary', href: secondaryBtnUrl.trim(), text: secondaryBtnText.trim(), className: 'elr-btn-outline', style: outlineBtnStyle }
      : null,
  ].filter(Boolean);

  if (liveButtons.length === 0) {
    return null;
  }

  return (
    <div className={`${className || ''}`} style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
      {liveButtons.map((button) => (
        <a key={button.key} href={button.href} className={button.className} style={button.style} target="_blank" rel="noopener noreferrer">
          {button.text}
        </a>
      ))}
    </div>
  );
}

/** Video URL editor row (shown in edit mode below video zone) */
function VideoUrlEditor({ sectionKey, videoUrl, videoTitle, isEditMode, onUpdateField }) {
  if (!isEditMode) return null;
  return (
    <div style={{ padding: '8px 12px', background: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
        <input
          type="text"
          defaultValue={videoTitle || ''}
          placeholder="Video section title"
          style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: 13 }}
          onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'videoTitle', e.target.value)}
          aria-label="Video title"
        />
        <input
          type="url"
          defaultValue={videoUrl || ''}
          placeholder="YouTube URL (e.g. https://www.youtube.com/watch?v=...)"
          style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 8px', fontSize: 13 }}
          onBlur={(e) => onUpdateField && onUpdateField(sectionKey, 'videoUrl', e.target.value)}
          aria-label="YouTube video URL"
        />
      </div>
    </div>
  );
}

/* ================================================================
   LAYOUT A — Centered Classic
   ================================================================ */
function LayoutA({ cd, getNavItems, isEditMode, onUpdateField, onUploadImage, uploadingFields }) {
  const navItems = getNavItems();
  const about = cd('about');
  const program = cd('program');
  const video = cd('video');
  const gallery = cd('gallery');
  const contact = cd('contact');
  const jobs = cd('jobs');
  const benefits = cd('benefits');
  const social = cd('social');
  const copyrightText = isEditMode ? (social.copyrightText || getDefaultCopyrightText()) : (social.copyrightText || '');
  const galleryImages = Array.isArray(gallery.images) ? gallery.images : Array(4).fill(null);
  const imgProps = {
    isEditMode,
    onUploadImage,
    onUpdateField,
    uploadingFields,
    resolveFitMode: (sectionKey, fieldName, fitFallback) => getImageFitMode(cd(sectionKey), fieldName, fitFallback),
  };
  const orderedMainKeys = navItems
    .map((item) => item.key)
    .filter((key) => ['about', 'program', 'video', 'gallery', 'contact', 'jobs', 'benefits'].includes(key));

  const renderSection = (key) => {
    if (key === 'about') {
      return (
        <SecWrap sectionKey="about" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-about" className="elr-bg-white" aria-label="About section">
          <div className="elr-a-hero-wrap">
            <ImgZone sectionKey="about" fieldName="heroImageUrl" url={about.heroImageUrl} alt={about.heroImageAlt || 'Hero banner'} altValue={about.heroImageAlt} label="Hero / Banner Image · full width" className="elr-a-hero-zone" {...imgProps} />
          </div>
          <div className="elr-sec" style={{ paddingTop: '2rem', textAlign: 'center' }}>
            <div className="elr-a-content">
              <TXT sectionKey="about" fieldName="companyName" value={about.companyName} placeholder="Company Name" className="elr-h1" tagName="h1" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              <div style={{ marginTop: '0.75rem' }} />
              <TXT sectionKey="about" fieldName="tagline" value={about.tagline} placeholder="Mission Statement / Tagline" className="elr-h2" style={{ fontSize: '1.2rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
              <div style={{ marginTop: '1rem' }} />
              <RichBody sectionKey="about" fieldName="aboutText" value={about.aboutText} placeholder="About Us paragraph..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            </div>
          </div>
        </SecWrap>
      );
    }
    if (key === 'program') {
      return (
        <SecWrap sectionKey="program" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-programs" className="elr-sec elr-bg-blue" aria-label="Programs section">
          <div className="elr-a-content elr-a-program">
            <ImgZone sectionKey="program" fieldName="programImageUrl" url={program.programImageUrl} alt={program.programImageAlt || 'Program image'} altValue={program.programImageAlt} label="Program Image · 960 × 540" className="elr-a-program-img" {...imgProps} />
            <TXT sectionKey="program" fieldName="programTitle" value={program.programTitle} placeholder="Special Program Title" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <RichBody sectionKey="program" fieldName="programText" value={program.programText} placeholder="Describe your special hiring initiative or program..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
        </SecWrap>
      );
    }
    if (key === 'video') {
      return (
        <SecWrap sectionKey="video" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-video" className="elr-sec elr-bg-white" aria-label="Video section">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <TXT sectionKey="video" fieldName="videoTitle" value={video.videoTitle} placeholder="Executive Welcome Video" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <VideoZone sectionKey="video" url={video.videoUrl} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <VideoUrlEditor sectionKey="video" videoUrl={video.videoUrl} videoTitle={video.videoTitle} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'gallery') {
      return (
        <SecWrap sectionKey="gallery" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-gallery" className="elr-sec elr-bg-light" aria-label="Gallery section">
          <TXT sectionKey="gallery" fieldName="galleryTitle" value={gallery.galleryTitle} placeholder="Life at the Company" className="elr-h2" tagName="h2" style={{ textAlign: 'center', marginBottom: '2rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <div className="elr-a-gallery">
            {[0, 1, 2, 3].map((i) => { const img = galleryImages[i] || {}; return (<ImgZone key={i} sectionKey="gallery" fieldName={`images.${i}`} url={img.url} alt={img.alt || `Gallery image ${i + 1}`} altValue={img.alt} label={`Gallery Image ${i + 1} · 600 × 400`} {...imgProps} />); })}
          </div>
        </SecWrap>
      );
    }
    if (key === 'contact') {
      return (
        <SecWrap sectionKey="contact" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-contact" className="elr-sec elr-bg-white" aria-label="Contact section">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center' }}>
            <TXT sectionKey="contact" fieldName="sectionHeading" value={contact.sectionHeading || 'Contact'} placeholder="Contact" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <CtaButtons sectionKey="contact" contentData={contact} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
        </SecWrap>
      );
    }
    if (key === 'jobs') {
      const hasJobsContent = (jobs.jobsList?.length > 0) || jobs.locationsText;
      if (!isEditMode && !hasJobsContent) return null;
      return (
        <SecWrap sectionKey="jobs" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-careers" className="elr-sec elr-bg-light" aria-label="Careers section">
          <div className="elr-a-content">
            <TXT sectionKey="jobs" fieldName="sectionHeading" value={jobs.sectionHeading || 'Open Positions'} placeholder="Open Positions" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <JobsList sectionKey="jobs" jobsList={jobs.jobsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            {!isEditMode && jobs.locationsText && (<div style={{ marginTop: '2.5rem' }}><h3 className="elr-h3" style={{ marginBottom: '0.5rem' }}>Locations</h3><p className="elr-body">{jobs.locationsText}</p></div>)}
            {isEditMode && (<div style={{ marginTop: 12 }}><label style={{ fontFamily: 'Arial,sans-serif', fontSize: 13, color: '#555' }}>Locations:</label><input type="text" defaultValue={jobs.locationsText || ''} placeholder="e.g. New York, San Francisco, Remote" style={{ width: '100%', marginTop: 4, border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 14 }} onBlur={(e) => onUpdateField && onUpdateField('jobs', 'locationsText', e.target.value)} aria-label="Locations" /></div>)}
          </div>
        </SecWrap>
      );
    }
    if (key === 'benefits') {
      if (!isEditMode && !hasHtmlText(getBenefitsHtml(benefits))) return null;
      return (
        <SecWrap sectionKey="benefits" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="a-benefits" className="elr-sec elr-bg-dark" aria-label="Benefits section">
          <div className="elr-a-content">
            <TXT sectionKey="benefits" fieldName="sectionHeading" value={benefits.sectionHeading || 'Benefits'} placeholder="Benefits" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <BenefitsList sectionKey="benefits" benefitsText={benefits.benefitsText} benefitsList={benefits.benefitsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
        </SecWrap>
      );
    }
    return null;
  };

  return (
    <>
      {/* Header */}
      {isEditMode && <MenuColorBar data={about} onUpdateField={onUpdateField} />}
      <header className="elr-a-header elr-bg-white" style={getMenuStyle(about)} role="banner">
        <ImgZone sectionKey="about" fieldName="logoImageUrl" url={about.logoImageUrl} alt={about.logoImageAlt || about.companyName || 'Company logo'} altValue={about.logoImageAlt} label="Company Logo · 240 × 100" className="elr-a-logo-zone" fitFallback="contain" {...imgProps} />
        <nav className="elr-a-nav" aria-label="Page sections">
          {renderNavLinks('a', navItems, 'elr-nav-link')}
        </nav>
      </header>

      <main>
        {orderedMainKeys.map((sectionKey, index) => (
          <React.Fragment key={sectionKey}>
            {renderSection(sectionKey)}
            {index < orderedMainKeys.length - 1 && <hr className="elr-divider" />}
          </React.Fragment>
        ))}
      </main>

      {/* Footer / Social */}
      <SecWrap sectionKey="social" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} className="elr-sec-sm elr-bg-white" style={{ textAlign: 'center', borderTop: '1px solid #e8e5e0' }} as="footer" role="contentinfo">
        <SocialFooter sectionKey="social" links={social.links} bgColor={social.bgColor} textColor={social.textColor} iconColor={social.iconColor} iconBgColor={social.iconBgColor} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        <TXT
          sectionKey="social"
          fieldName="copyrightText"
          value={copyrightText}
          placeholder="Copyright text"
          tagName="p"
          style={{ fontFamily: 'monospace', fontSize: 12, color: '#aaa', marginTop: 12 }}
          isEditMode={isEditMode}
          onUpdateField={onUpdateField}
        />
      </SecWrap>
    </>
  );
}

/* ================================================================
   LAYOUT B — Split / Square Images
   ================================================================ */
function LayoutB({ cd, getNavItems, isEditMode, onUpdateField, onUploadImage, uploadingFields }) {
  const navItems = getNavItems();
  const about = cd('about');
  const program = cd('program');
  const video = cd('video');
  const gallery = cd('gallery');
  const contact = cd('contact');
  const jobs = cd('jobs');
  const benefits = cd('benefits');
  const social = cd('social');
  const copyrightText = isEditMode ? (social.copyrightText || getDefaultCopyrightText()) : (social.copyrightText || '');
  const galleryImages = Array.isArray(gallery.images) ? gallery.images : Array(4).fill(null);
  const imgProps = {
    isEditMode,
    onUploadImage,
    onUpdateField,
    uploadingFields,
    resolveFitMode: (sectionKey, fieldName, fitFallback) => getImageFitMode(cd(sectionKey), fieldName, fitFallback),
  };
  const hasAboutDualImages = isEditMode || hasText(about.brandImage1Url) || hasText(about.brandImage2Url);
  const orderedMainKeys = navItems
    .map((item) => item.key)
    .filter((key) => ['about', 'program', 'video', 'gallery', 'contact', 'jobs', 'benefits'].includes(key));

  const renderSection = (key) => {
    if (key === 'about') {
      return (
        <React.Fragment>
          {hasAboutDualImages && (
            <div className="elr-dual-images elr-bg-white" role="group" aria-label="Company identity images">
              <ImgZone sectionKey="about" fieldName="brandImage1Url" url={about.brandImage1Url} alt={about.brandImage1Alt || 'Brand image 1'} altValue={about.brandImage1Alt} label="Brand Image 1 · full-width banner" {...imgProps} />
              <ImgZone sectionKey="about" fieldName="brandImage2Url" url={about.brandImage2Url} alt={about.brandImage2Alt || 'Brand image 2'} altValue={about.brandImage2Alt} label="Brand Image 2 · full-width banner" {...imgProps} />
            </div>
          )}
          <SecWrap sectionKey="about" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-about" className="elr-bg-white" aria-label="About section">
            <div className="elr-b-about-row">
              <ImgZone sectionKey="about" fieldName="heroImageUrl" url={about.heroImageUrl} alt={about.heroImageAlt || 'Company image'} altValue={about.heroImageAlt} label="Company Image · ~340 × 340 square" className="elr-b-about-img-zone" {...imgProps} />
              <div className="elr-b-about-txt">
                <TXT sectionKey="about" fieldName="companyName" value={about.companyName} placeholder="Company Name" className="elr-h1" tagName="h1" isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <TXT sectionKey="about" fieldName="tagline" value={about.tagline} placeholder="Mission Statement / Tagline" className="elr-h2" style={{ fontSize: '1.15rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <RichBody sectionKey="about" fieldName="aboutText" value={about.aboutText} placeholder="About Us paragraph..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              </div>
            </div>
          </SecWrap>
        </React.Fragment>
      );
    }
    if (key === 'program') {
      return (
        <SecWrap sectionKey="program" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-programs" className="elr-bg-blue" aria-label="Programs section">
          <div className="elr-b-prog-row">
            <div className="elr-b-prog-txt">
              <TXT sectionKey="program" fieldName="programTitle" value={program.programTitle} placeholder="Special Program Title" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              <RichBody sectionKey="program" fieldName="programText" value={program.programText} placeholder="Describe your special hiring initiative..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            </div>
            <ImgZone sectionKey="program" fieldName="programImageUrl" url={program.programImageUrl} alt={program.programImageAlt || 'Program image'} altValue={program.programImageAlt} label="Program Image · ~300 × 300 square" className="elr-b-prog-img-zone" {...imgProps} />
          </div>
        </SecWrap>
      );
    }
    if (key === 'video') {
      return (
        <SecWrap sectionKey="video" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-video" className="elr-sec elr-bg-white" aria-label="Video section">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <TXT sectionKey="video" fieldName="videoTitle" value={video.videoTitle} placeholder="Executive Welcome Video" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <VideoZone sectionKey="video" url={video.videoUrl} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <VideoUrlEditor sectionKey="video" videoUrl={video.videoUrl} videoTitle={video.videoTitle} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'gallery') {
      return (
        <SecWrap sectionKey="gallery" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-gallery" className="elr-bg-light" aria-label="Gallery section">
          <div style={{ padding: '1.5rem 3rem 0.75rem' }}>
            <TXT sectionKey="gallery" fieldName="galleryTitle" value={gallery.galleryTitle} placeholder="Life at the Company" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <div className="elr-b-gal-row">
            {[0, 1, 2, 3].map((i) => { const img = galleryImages[i] || {}; return (<ImgZone key={i} sectionKey="gallery" fieldName={`images.${i}`} url={img.url} alt={img.alt || `Gallery image ${i + 1}`} altValue={img.alt} label={`Image ${i + 1} · ~260 × 260`} {...imgProps} />); })}
          </div>
        </SecWrap>
      );
    }
    if (key === 'contact') {
      return (
        <SecWrap sectionKey="contact" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-contact" className="elr-sec elr-bg-blue" aria-label="Contact section">
          <TXT sectionKey="contact" fieldName="sectionHeading" value={contact.sectionHeading || 'Contact'} placeholder="Contact" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <CtaButtons sectionKey="contact" contentData={contact} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'jobs') {
      const hasJobsContent = (jobs.jobsList?.length > 0) || jobs.locationsText;
      if (!isEditMode && !hasJobsContent) return null;
      return (
        <SecWrap sectionKey="jobs" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-careers" className="elr-sec elr-bg-light" aria-label="Careers section">
          <TXT sectionKey="jobs" fieldName="sectionHeading" value={jobs.sectionHeading || 'Open Positions'} placeholder="Open Positions" className="elr-h2" tagName="h2" style={{ marginBottom: '1.25rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <JobsList sectionKey="jobs" jobsList={jobs.jobsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          {!isEditMode && jobs.locationsText && (<div style={{ marginTop: '1.5rem' }}><h3 className="elr-h3" style={{ marginBottom: '0.4rem' }}>Locations</h3><p className="elr-body" style={{ fontSize: '0.9rem' }}>{jobs.locationsText}</p></div>)}
          {isEditMode && (<div style={{ marginTop: 10 }}><input type="text" defaultValue={jobs.locationsText || ''} placeholder="Locations" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 13 }} onBlur={(e) => onUpdateField && onUpdateField('jobs', 'locationsText', e.target.value)} aria-label="Locations" /></div>)}
        </SecWrap>
      );
    }
    if (key === 'benefits') {
      if (!isEditMode && !hasHtmlText(getBenefitsHtml(benefits))) return null;
      return (
        <SecWrap sectionKey="benefits" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="b-benefits" className="elr-b-ben-col elr-bg-dark" aria-label="Benefits section">
          <TXT sectionKey="benefits" fieldName="sectionHeading" value={benefits.sectionHeading || 'Benefits'} placeholder="Benefits" className="elr-h2" tagName="h2" style={{ marginBottom: '1.25rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <BenefitsList sectionKey="benefits" benefitsText={benefits.benefitsText} benefitsList={benefits.benefitsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    return null;
  };

  return (
    <>
      {/* Header: logo left, nav right */}
      {isEditMode && <MenuColorBar data={about} onUpdateField={onUpdateField} />}
      <header className="elr-b-header elr-bg-white" style={getMenuStyle(about)} role="banner">
        <div className="elr-b-logo-col">
          <ImgZone sectionKey="about" fieldName="logoImageUrl" url={about.logoImageUrl} alt={about.logoImageAlt || about.companyName || 'Company logo'} altValue={about.logoImageAlt} label="Logo · 150 × 80" className="elr-b-logo-zone" fitFallback="contain" {...imgProps} />
        </div>
        <div className="elr-b-nav-col">
          <nav className="elr-b-nav" aria-label="Page sections">
            {renderNavLinks('b', navItems, 'elr-nav-link')}
          </nav>
        </div>
      </header>

      <main>
        {orderedMainKeys.map((sectionKey, index) => (
          <React.Fragment key={sectionKey}>
            {renderSection(sectionKey)}
            {index < orderedMainKeys.length - 1 && <hr className="elr-divider" />}
          </React.Fragment>
        ))}
      </main>

      <SecWrap sectionKey="social" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} className="elr-sec-sm elr-bg-white" style={{ textAlign: 'center', borderTop: '1px solid #e8e5e0' }} as="footer" role="contentinfo">
        <SocialFooter sectionKey="social" links={social.links} bgColor={social.bgColor} textColor={social.textColor} iconColor={social.iconColor} iconBgColor={social.iconBgColor} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        <TXT
          sectionKey="social"
          fieldName="copyrightText"
          value={copyrightText}
          placeholder="Copyright text"
          tagName="p"
          style={{ fontFamily: 'monospace', fontSize: 12, color: '#aaa', marginTop: 12 }}
          isEditMode={isEditMode}
          onUpdateField={onUpdateField}
        />
      </SecWrap>
    </>
  );
}

/* ================================================================
   LAYOUT C — Editorial / Mosaic
   ================================================================ */
function LayoutC({ cd, getNavItems, isEditMode, onUpdateField, onUploadImage, uploadingFields }) {
  const navItems = getNavItems();
  const about = cd('about');
  const program = cd('program');
  const video = cd('video');
  const gallery = cd('gallery');
  const contact = cd('contact');
  const jobs = cd('jobs');
  const benefits = cd('benefits');
  const social = cd('social');
  const copyrightText = isEditMode ? (social.copyrightText || getDefaultCopyrightText()) : (social.copyrightText || '');
  const galleryImages = Array.isArray(gallery.images) ? gallery.images : Array(5).fill(null);
  const imgProps = {
    isEditMode,
    onUploadImage,
    onUpdateField,
    uploadingFields,
    resolveFitMode: (sectionKey, fieldName, fitFallback) => getImageFitMode(cd(sectionKey), fieldName, fitFallback),
  };
  const hasAboutDualImages = isEditMode || hasText(about.brandImage1Url) || hasText(about.brandImage2Url);
  const orderedMainKeys = navItems
    .map((item) => item.key)
    .filter((key) => ['about', 'program', 'video', 'gallery', 'contact', 'jobs', 'benefits'].includes(key));

  const renderSection = (key) => {
    if (key === 'about') {
      return (
        <React.Fragment>
          {hasAboutDualImages && (
            <div className="elr-dual-images elr-bg-white" role="group" aria-label="Company identity images">
              <ImgZone sectionKey="about" fieldName="brandImage1Url" url={about.brandImage1Url} alt={about.brandImage1Alt || 'Brand image 1'} altValue={about.brandImage1Alt} label="Brand Image 1 · full-width banner" {...imgProps} />
              <ImgZone sectionKey="about" fieldName="brandImage2Url" url={about.brandImage2Url} alt={about.brandImage2Alt || 'Brand image 2'} altValue={about.brandImage2Alt} label="Brand Image 2 · full-width banner" {...imgProps} />
            </div>
          )}
          <SecWrap sectionKey="about" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-about" className="elr-sec elr-bg-white" aria-label="About section">
            <div className="elr-c-about-row">
              <div>
                <TXT sectionKey="about" fieldName="companyName" value={about.companyName} placeholder="Company Name" className="elr-h1" tagName="h1" isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <div style={{ marginTop: '0.75rem' }} />
                <TXT sectionKey="about" fieldName="tagline" value={about.tagline} placeholder="Mission Statement / Tagline" className="elr-h2" style={{ fontSize: '1.15rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <div style={{ marginTop: '1rem' }} />
                <RichBody sectionKey="about" fieldName="aboutText" value={about.aboutText} placeholder="About Us paragraph..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              </div>
              <ImgZone sectionKey="about" fieldName="heroImageUrl" url={about.heroImageUrl} alt={about.heroImageAlt || 'Accent portrait'} altValue={about.heroImageAlt} label="Accent Portrait · 3:4" className="elr-c-about-portrait" {...imgProps} />
            </div>
          </SecWrap>
        </React.Fragment>
      );
    }
    if (key === 'program') {
      return (
        <SecWrap sectionKey="program" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-programs" className="elr-bg-light" aria-label="Programs section">
          <div style={{ position: 'relative' }}>
            <ImgZone sectionKey="program" fieldName="programImageUrl" url={program.programImageUrl} alt={program.programImageAlt || 'Program image'} altValue={program.programImageAlt} label="Program Image · Full Width × 320px" className="elr-c-prog-full" {...imgProps} />
            <div className="elr-c-prog-overlay">
              <TXT sectionKey="program" fieldName="programTitle" value={program.programTitle} placeholder="Special Program Title" className="elr-h2" tagName="h2" style={{ color: '#fff', marginBottom: '0.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
              <RichBody sectionKey="program" fieldName="programText" value={program.programText} placeholder="Describe your special initiative..." className="elr-body" style={{ fontFamily: 'Arial,sans-serif', fontSize: 14, color: program.programText__color || '#b8c8e0', lineHeight: 1.6, maxWidth: 680, display: 'block' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            </div>
          </div>
        </SecWrap>
      );
    }
    if (key === 'video') {
      return (
        <SecWrap sectionKey="video" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-video" className="elr-sec elr-bg-white" aria-label="Video section">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <TXT sectionKey="video" fieldName="videoTitle" value={video.videoTitle} placeholder="Executive Welcome Video" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <VideoZone sectionKey="video" url={video.videoUrl} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <VideoUrlEditor sectionKey="video" videoUrl={video.videoUrl} videoTitle={video.videoTitle} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'gallery') {
      return (
        <SecWrap sectionKey="gallery" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-gallery" className="elr-sec elr-bg-blue" aria-label="Gallery section">
          <TXT sectionKey="gallery" fieldName="galleryTitle" value={gallery.galleryTitle} placeholder="Life at the Company" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <div className="elr-c-mosaic">
            <ImgZone sectionKey="gallery" fieldName="images.0" url={galleryImages[0]?.url} alt={galleryImages[0]?.alt || 'Gallery 1'} altValue={galleryImages[0]?.alt} label="Image 1 — Large · ~460 × 360" className="elr-c-mosaic-big" {...imgProps} />
            <ImgZone sectionKey="gallery" fieldName="images.1" url={galleryImages[1]?.url} alt={galleryImages[1]?.alt || 'Gallery 2'} altValue={galleryImages[1]?.alt} label="Image 2 · ~220 × 175" className="elr-c-mosaic-sm" {...imgProps} />
            <ImgZone sectionKey="gallery" fieldName="images.2" url={galleryImages[2]?.url} alt={galleryImages[2]?.alt || 'Gallery 3'} altValue={galleryImages[2]?.alt} label="Image 3 · ~220 × 175" className="elr-c-mosaic-sm" {...imgProps} />
            <ImgZone sectionKey="gallery" fieldName="images.3" url={galleryImages[3]?.url} alt={galleryImages[3]?.alt || 'Gallery 4'} altValue={galleryImages[3]?.alt} label="Image 4 · ~220 × 175" className="elr-c-mosaic-land" {...imgProps} />
            <ImgZone sectionKey="gallery" fieldName="images.4" url={galleryImages[4]?.url} alt={galleryImages[4]?.alt || 'Gallery 5'} altValue={galleryImages[4]?.alt} label="Image 5 · ~220 × 175" className="elr-c-mosaic-land" {...imgProps} />
          </div>
        </SecWrap>
      );
    }
    if (key === 'contact') {
      return (
        <SecWrap sectionKey="contact" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-contact" className="elr-sec elr-bg-dark" aria-label="Contact section">
          <div style={{ textAlign: 'center' }}>
            <TXT sectionKey="contact" fieldName="sectionHeading" value={contact.sectionHeading || 'Ready to Join Our Team?'} placeholder="Ready to Join Our Team?" className="elr-h2" tagName="h2" style={{ marginBottom: '0.75rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            {isEditMode && (
              <p className="elr-body" style={{ marginBottom: '1.5rem', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
                Explore open roles or connect with our talent team.
              </p>
            )}
            <CtaButtons sectionKey="contact" contentData={contact} isEditMode={isEditMode} onUpdateField={onUpdateField} className="justify-center" />
          </div>
        </SecWrap>
      );
    }
    if (key === 'jobs') {
      const hasJobsContent = (jobs.jobsList?.length > 0) || jobs.locationsText;
      if (!isEditMode && !hasJobsContent) return null;
      return (
        <SecWrap sectionKey="jobs" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-careers" className="elr-sec elr-bg-light" aria-label="Careers section">
          <div style={{ maxWidth: 860 }}>
            <TXT sectionKey="jobs" fieldName="sectionHeading" value={jobs.sectionHeading || 'Open Positions'} placeholder="Open Positions" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <JobsList sectionKey="jobs" jobsList={jobs.jobsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            {!isEditMode && jobs.locationsText && (<div style={{ marginTop: '2rem' }}><h3 className="elr-h3" style={{ marginBottom: '0.4rem' }}>Locations</h3><p className="elr-body">{jobs.locationsText}</p></div>)}
            {isEditMode && (<div style={{ marginTop: 10 }}><input type="text" defaultValue={jobs.locationsText || ''} placeholder="Locations" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 13 }} onBlur={(e) => onUpdateField && onUpdateField('jobs', 'locationsText', e.target.value)} aria-label="Locations" /></div>)}
          </div>
        </SecWrap>
      );
    }
    if (key === 'benefits') {
      if (!isEditMode && !hasHtmlText(getBenefitsHtml(benefits))) return null;
      return (
        <SecWrap sectionKey="benefits" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="c-benefits" className="elr-sec elr-bg-dark" aria-label="Benefits section">
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <TXT sectionKey="benefits" fieldName="sectionHeading" value={benefits.sectionHeading || 'Benefits'} placeholder="Benefits" className="elr-h2" tagName="h2" style={{ marginBottom: '1.5rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <BenefitsList sectionKey="benefits" benefitsText={benefits.benefitsText} benefitsList={benefits.benefitsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
        </SecWrap>
      );
    }
    return null;
  };

  return (
    <>
      {/* Header: logo + nav inline */}
      {isEditMode && <MenuColorBar data={about} onUpdateField={onUpdateField} />}
      <header className="elr-c-header elr-bg-white" style={getMenuStyle(about)} role="banner">
        <ImgZone sectionKey="about" fieldName="logoImageUrl" url={about.logoImageUrl} alt={about.logoImageAlt || about.companyName || 'Company logo'} altValue={about.logoImageAlt} label="Logo · 110 × 48" className="elr-c-logo-zone" fitFallback="contain" {...imgProps} />
        <nav className="elr-c-nav" aria-label="Page sections">
          {renderNavLinks('c', navItems, 'elr-nav-link')}
        </nav>
      </header>

      <main>
        {orderedMainKeys.map((sectionKey, index) => (
          <React.Fragment key={sectionKey}>
            {renderSection(sectionKey)}
            {index < orderedMainKeys.length - 1 && <hr className="elr-divider" />}
          </React.Fragment>
        ))}
      </main>

      <SecWrap sectionKey="social" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} className="elr-sec-sm elr-bg-white" style={{ textAlign: 'center', borderTop: '1px solid #e8e5e0' }} as="footer" role="contentinfo">
        <SocialFooter sectionKey="social" links={social.links} bgColor={social.bgColor} textColor={social.textColor} iconColor={social.iconColor} iconBgColor={social.iconBgColor} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        <TXT
          sectionKey="social"
          fieldName="copyrightText"
          value={copyrightText}
          placeholder="Copyright text"
          tagName="p"
          style={{ fontFamily: 'monospace', fontSize: 12, color: '#aaa', marginTop: 12 }}
          isEditMode={isEditMode}
          onUpdateField={onUpdateField}
        />
      </SecWrap>
    </>
  );
}

/* ================================================================
   LAYOUT D — Compact / Inline
   ================================================================ */
function LayoutD({ cd, getNavItems, isEditMode, onUpdateField, onUploadImage, uploadingFields }) {
  const navItems = getNavItems();
  const about = cd('about');
  const program = cd('program');
  const video = cd('video');
  const gallery = cd('gallery');
  const contact = cd('contact');
  const jobs = cd('jobs');
  const benefits = cd('benefits');
  const social = cd('social');
  const copyrightText = isEditMode ? (social.copyrightText || getDefaultCopyrightText()) : (social.copyrightText || '');
  const galleryImages = Array.isArray(gallery.images) ? gallery.images : Array(4).fill(null);
  const imgProps = {
    isEditMode,
    onUploadImage,
    onUpdateField,
    uploadingFields,
    resolveFitMode: (sectionKey, fieldName, fitFallback) => getImageFitMode(cd(sectionKey), fieldName, fitFallback),
  };
  const hasAboutDualImages = isEditMode || hasText(about.brandImage1Url) || hasText(about.brandImage2Url);
  const orderedMainKeys = navItems
    .map((item) => item.key)
    .filter((key) => ['about', 'program', 'video', 'gallery', 'contact', 'jobs', 'benefits'].includes(key));

  const renderSection = (key) => {
    if (key === 'about') {
      return (
        <React.Fragment>
          <SecWrap sectionKey="about" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-about" className="elr-bg-white" aria-label="About section">
            <div className="elr-d-identity">
              <ImgZone sectionKey="about" fieldName="heroImageUrl" url={about.heroImageUrl} alt={about.heroImageAlt || 'Identity image'} altValue={about.heroImageAlt} label="Identity Image · ~300 × 280" className="elr-d-id-img-zone" {...imgProps} />
              <div className="elr-d-id-txt">
                <TXT sectionKey="about" fieldName="companyName" value={about.companyName} placeholder="Company Name" className="elr-h1" tagName="h1" isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <TXT sectionKey="about" fieldName="tagline" value={about.tagline} placeholder="Mission Statement / Tagline" className="elr-h2" style={{ fontSize: '1.05rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
                <RichBody sectionKey="about" fieldName="aboutText" value={about.aboutText} placeholder="About Us paragraph..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              </div>
            </div>
          </SecWrap>
          {hasAboutDualImages && (
            <div className="elr-dual-images elr-bg-white" role="group" aria-label="Additional company images">
              <ImgZone sectionKey="about" fieldName="brandImage1Url" url={about.brandImage1Url} alt={about.brandImage1Alt || 'Brand image 1'} altValue={about.brandImage1Alt} label="Brand Image 1 · full-width banner" {...imgProps} />
              <ImgZone sectionKey="about" fieldName="brandImage2Url" url={about.brandImage2Url} alt={about.brandImage2Alt || 'Brand image 2'} altValue={about.brandImage2Alt} label="Brand Image 2 · full-width banner" {...imgProps} />
            </div>
          )}
        </React.Fragment>
      );
    }
    if (key === 'program') {
      return (
        <SecWrap sectionKey="program" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-programs" className="elr-sec elr-bg-blue" aria-label="Programs section">
          <div className="elr-d-prog-row">
            <div className="elr-d-prog-txt">
              <TXT sectionKey="program" fieldName="programTitle" value={program.programTitle} placeholder="Special Program Title" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
              <RichBody sectionKey="program" fieldName="programText" value={program.programText} placeholder="Describe your special initiative..." className="elr-body" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            </div>
            <ImgZone sectionKey="program" fieldName="programImageUrl" url={program.programImageUrl} alt={program.programImageAlt || 'Program image'} altValue={program.programImageAlt} label="Program Image · Landscape 4:3" className="elr-d-prog-img-zone" {...imgProps} />
          </div>
        </SecWrap>
      );
    }
    if (key === 'video') {
      return (
        <SecWrap sectionKey="video" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-video" className="elr-sec elr-bg-white" aria-label="Video section">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <TXT sectionKey="video" fieldName="videoTitle" value={video.videoTitle} placeholder="Executive Welcome Video" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
            <VideoZone sectionKey="video" url={video.videoUrl} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <VideoUrlEditor sectionKey="video" videoUrl={video.videoUrl} videoTitle={video.videoTitle} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'gallery') {
      return (
        <SecWrap sectionKey="gallery" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-gallery" className="elr-bg-light" aria-label="Gallery section">
          <div style={{ padding: '1.5rem 3rem 0.5rem' }}>
            <TXT sectionKey="gallery" fieldName="galleryTitle" value={gallery.galleryTitle} placeholder="Life at the Company" className="elr-h2" tagName="h2" isEditMode={isEditMode} onUpdateField={onUpdateField} />
          </div>
          <div className="elr-d-gal-row">
            {[0, 1, 2, 3].map((i) => { const img = galleryImages[i] || {}; return (<ImgZone key={i} sectionKey="gallery" fieldName={`images.${i}`} url={img.url} alt={img.alt || `Gallery image ${i + 1}`} altValue={img.alt} label={`Image ${i + 1} · ~300 × 200`} {...imgProps} />); })}
          </div>
        </SecWrap>
      );
    }
    if (key === 'contact') {
      return (
        <SecWrap sectionKey="contact" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-contact" className="elr-d-cta-banner elr-bg-blue" aria-label="Contact section">
          <TXT sectionKey="contact" fieldName="ctaHeadline" value={contact.ctaHeadline} placeholder="Ready to explore a career with us?" className="elr-h2" tagName="h2" style={{ fontSize: '1.15rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <CtaButtons sectionKey="contact" contentData={contact} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    if (key === 'jobs') {
      const hasJobsContent = (jobs.jobsList?.length > 0) || jobs.locationsText;
      if (!isEditMode && !hasJobsContent) return null;
      return (
        <SecWrap sectionKey="jobs" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-careers" className="elr-sec elr-bg-light" aria-label="Careers section">
          <TXT sectionKey="jobs" fieldName="sectionHeading" value={jobs.sectionHeading || 'Open Positions'} placeholder="Open Positions" className="elr-h2" tagName="h2" style={{ marginBottom: '1.25rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <JobsList sectionKey="jobs" jobsList={jobs.jobsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          {!isEditMode && jobs.locationsText && (<div style={{ marginTop: '1.5rem' }}><h3 className="elr-h3" style={{ marginBottom: '0.4rem' }}>Locations</h3><p className="elr-body" style={{ fontSize: '0.9rem' }}>{jobs.locationsText}</p></div>)}
          {isEditMode && (<div style={{ marginTop: 10 }}><input type="text" defaultValue={jobs.locationsText || ''} placeholder="Locations" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: 13 }} onBlur={(e) => onUpdateField && onUpdateField('jobs', 'locationsText', e.target.value)} aria-label="Locations" /></div>)}
        </SecWrap>
      );
    }
    if (key === 'benefits') {
      if (!isEditMode && !hasHtmlText(getBenefitsHtml(benefits))) return null;
      return (
        <SecWrap sectionKey="benefits" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} id="d-benefits" className="elr-d-ben-col" aria-label="Benefits section">
          <TXT sectionKey="benefits" fieldName="sectionHeading" value={benefits.sectionHeading || 'Benefits'} placeholder="Benefits" className="elr-h2" tagName="h2" style={{ marginBottom: '1rem' }} isEditMode={isEditMode} onUpdateField={onUpdateField} />
          <BenefitsList sectionKey="benefits" benefitsText={benefits.benefitsText} benefitsList={benefits.benefitsList} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        </SecWrap>
      );
    }
    return null;
  };

  return (
    <>
      {/* Dark header */}
      {isEditMode && <MenuColorBar data={about} onUpdateField={onUpdateField} />}
      <header className="elr-d-header" style={getMenuStyle(about)} role="banner">
        <ImgZone sectionKey="about" fieldName="logoImageUrl" url={about.logoImageUrl} alt={about.logoImageAlt || about.companyName || 'Company logo'} altValue={about.logoImageAlt} label="Logo · 95 × 40" className="elr-d-logo-zone" style={{ background: '#2d3a50', borderColor: '#445' }} fitFallback="contain" {...imgProps} />
        <nav className="elr-d-nav" aria-label="Page sections">
          {renderNavLinks('d', navItems)}
        </nav>
      </header>

      <main>
        {orderedMainKeys.map((sectionKey, index) => (
          <React.Fragment key={sectionKey}>
            {renderSection(sectionKey)}
            {index < orderedMainKeys.length - 1 && <hr className="elr-divider" />}
          </React.Fragment>
        ))}
      </main>

      <SecWrap sectionKey="social" cd={cd} isEditMode={isEditMode} onUpdateField={onUpdateField} className="elr-sec-sm elr-bg-white" style={{ textAlign: 'center', borderTop: '1px solid #e8e5e0' }} as="footer" role="contentinfo">
        <SocialFooter sectionKey="social" links={social.links} bgColor={social.bgColor} textColor={social.textColor} iconColor={social.iconColor} iconBgColor={social.iconBgColor} isEditMode={isEditMode} onUpdateField={onUpdateField} />
        <TXT
          sectionKey="social"
          fieldName="copyrightText"
          value={copyrightText}
          placeholder="Copyright text"
          tagName="p"
          style={{ fontFamily: 'monospace', fontSize: 12, color: '#aaa', marginTop: 12 }}
          isEditMode={isEditMode}
          onUpdateField={onUpdateField}
        />
      </SecWrap>
    </>
  );
}
